import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calendar, TrendingUp, Target, Award, Users, BarChart } from 'lucide-react';

const ZeturfNews = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Calendar className="h-6 w-6 text-book-brown" />
              <span className="text-book-brown-dark font-medium">12.12.2025</span>
              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Promotions</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Zeturf's Holiday Racing Festival Promises Record Jackpots
            </h1>
            <p className="text-xl text-book-brown-dark leading-relaxed">
              The French racing specialist announces unprecedented prize pools for the holiday season, with €50,000+ guaranteed jackpots
              across selected races and enhanced betting opportunities for international players.
            </p>
          </div>

          <div className="mb-12 bg-gradient-to-br from-book-brown to-book-brown-light rounded-xl p-8 text-center text-book-cream">
            <TrendingUp className="h-16 w-16 mx-auto mb-4 opacity-80" />
            <img
              src="/images/carl-raw-dmc4sVdnSDs-unsplash.webp"
              alt="Zeturf Racing Festival"
              className="w-full h-64 object-cover rounded-lg"
            />
            <p className="text-sm opacity-75">Holiday Racing Spectacular</p>
          </div>

          <div className="mb-16 bg-book-beige rounded-xl p-8 shadow-book-inset">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Our News Curation Philosophy</h2>
            <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
              <p className="mb-4">
                In an industry where information flows like a raging river, Brit Ember stands as the discerning curator,
                carefully selecting only the most significant developments that genuinely impact UK casino players...
              </p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-6">Unprecedented Holiday Racing Spectacle</h2>

            <p className="mb-6">
              Zeturf's holiday racing festival represents a paradigm shift in seasonal promotions, combining French racing
              expertise with international appeal to create Europe's most anticipated winter betting event. This strategic
              initiative transcends traditional holiday promotions, establishing new benchmarks for prize pool generosity
              and betting innovation that will redefine how the industry approaches seasonal celebrations.
            </p>

            <p className="mb-6">
              The €50,000+ guaranteed jackpots across selected races represent more than impressive figures – they symbolize
              Zeturf's commitment to creating life-changing opportunities for bettors worldwide. This unprecedented financial
              commitment demonstrates how premium operators can transform seasonal promotions into strategic market
              differentiation that attracts discerning players seeking genuine value.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Record-Breaking Prize Pool Strategy</h3>

            <p className="mb-6">
              Zeturf's jackpot structure represents a masterclass in promotional mathematics. The €50,000+ guarantees
              across multiple races create multiple life-changing opportunities during the holiday season, strategically
              distributed to maximize player engagement while maintaining manageable operational costs. This approach
              transforms traditional rollover mechanics into player-centric opportunities that reward both casual
              participants and serious bettors.
            </p>

            <p className="mb-6">
              The multi-race jackpot distribution ensures continuous excitement throughout the festival period, with
              progressive prizes that grow in value as the season advances. This strategic design creates sustained
              engagement that extends beyond individual races to encompass the entire holiday experience, maximizing
              both entertainment value and betting volume.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Enhanced International Accessibility</h3>

            <p className="mb-6">
              Zeturf's international expansion strategy reaches new heights with the holiday festival, offering enhanced
              betting opportunities specifically designed for global audiences. Multi-language interfaces, international
              payment options, and culturally adapted promotional content ensure that players from diverse markets
              can fully participate in this unprecedented racing spectacular.
            </p>

            <p className="mb-6">
              The platform's international betting tools provide sophisticated analysis for global bettors, combining
              French racing expertise with international market perspectives. This cross-cultural approach creates
              betting opportunities that transcend geographical boundaries, offering UK players access to continental
              racing insights that domestic operators cannot match.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Technological Innovation in Racing Entertainment</h3>

            <p className="mb-6">
              The festival introduces cutting-edge racing entertainment features that revolutionize how bettors experience
              horse racing. Advanced streaming technology, interactive race analytics, and real-time betting tools
              transform passive viewing into immersive betting experiences that combine entertainment with strategic
              decision-making.
            </p>

            <p className="mb-6">
              Zeturf's technological approach integrates virtual reality previews, predictive analytics, and social
              betting features that enhance the racing experience. These innovations create new dimensions of engagement
              that go beyond traditional betting, establishing Zeturf as a technological leader in racing entertainment.
            </p>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-book-brown mb-8 text-center">Holiday Racing Festival Analytics</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Prize Pool Distribution</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Main Jackpot</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-yellow-500 h-3 rounded-full" style={{width: '60%'}}></div>
                      </div>
                      <span className="font-semibold text-yellow-600">€75K+</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Secondary Pots</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '80%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">€50K+</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">Daily Bonuses</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-blue-500 h-3 rounded-full" style={{width: '40%'}}></div>
                      </div>
                      <span className="font-semibold text-blue-600">€10K+</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Race Schedule Intensity</h3>
                <div className="flex justify-center mb-4">
                  <div className="relative w-48 h-48">
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#FFD700" strokeWidth="20"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#8B4513" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="31.42"/>
                      <circle cx="50" cy="50" r="40" fill="none" stroke="#228B22" strokeWidth="20" strokeDasharray="125.66" strokeDashoffset="62.83"/>
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-book-brown">85%</span>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>Peak Festival Days</span>
                    <span className="font-semibold">85%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Regular Season</span>
                    <span className="font-semibold">65%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Off-Peak Periods</span>
                    <span className="font-semibold">45%</span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-book p-6">
                <h3 className="text-xl font-bold text-book-brown mb-4 text-center">Global Participation Growth</h3>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">UK Bettors</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-green-500 h-3 rounded-full" style={{width: '90%'}}></div>
                      </div>
                      <span className="font-semibold text-green-600">+90%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">European Bettors</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-blue-500 h-3 rounded-full" style={{width: '75%'}}></div>
                      </div>
                      <span className="font-semibold text-blue-600">+75%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-book-brown-dark">International</span>
                      <div className="flex-1 mx-4 bg-gray-200 rounded-full h-3">
                        <div className="bg-purple-500 h-3 rounded-full" style={{width: '60%'}}></div>
                      </div>
                      <span className="font-semibold text-purple-600">+60%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
            <h3 className="text-2xl font-bold text-book-brown mb-4">Market Impact and Industry Influence</h3>

            <p className="mb-6">
              Zeturf's holiday festival establishes new promotional benchmarks that will influence the entire racing
              industry. The €50,000+ jackpots create competitive pressure that elevates promotional standards across
              European racing platforms, benefiting bettors through improved offerings and enhanced value propositions.
              This strategic initiative demonstrates how premium operators can drive market-wide improvements through
              innovative promotional strategies.
            </p>

            <p className="mb-6">
              The festival's international accessibility sets new standards for global betting participation. By removing
              geographical barriers and providing culturally adapted experiences, Zeturf creates inclusive betting
              environments that maximize participation while maintaining regulatory compliance and responsible gambling
              standards.
            </p>

            <h3 className="text-2xl font-bold text-book-brown mb-4">Technological Leadership in Racing</h3>

            <p className="mb-6">
              Zeturf's technological innovations during the holiday festival position the platform as a leader in racing
              entertainment. Advanced streaming capabilities, predictive analytics, and interactive betting tools create
              immersive experiences that go beyond traditional race watching, establishing new standards for how bettors
              engage with horse racing.
            </p>

            <p className="mb-6">
              The platform's commitment to technological excellence extends to user experience optimization, with
              intuitive interfaces that make complex betting strategies accessible to players at all experience levels.
              This democratic approach to advanced betting tools ensures that technological innovation benefits all
              participants rather than creating exclusive advantages for expert bettors.
            </p>

            <div className="bg-book-beige rounded-lg p-6 my-8">
              <h4 className="text-xl font-bold text-book-brown mb-4">Strategic Advantages for UK Bettors</h4>
              <ul className="space-y-2">
                <li className="flex items-start">
                  <Target className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Unprecedented jackpot opportunities create life-changing betting potential</span>
                </li>
                <li className="flex items-start">
                  <Award className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Access to French racing expertise and continental betting strategies</span>
                </li>
                <li className="flex items-start">
                  <Users className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Enhanced international betting community and social interaction</span>
                </li>
                <li className="flex items-start">
                  <BarChart className="h-5 w-5 text-book-brown mr-3 mt-0.5 flex-shrink-0" />
                  <span>Advanced technological tools and innovative betting experiences</span>
                </li>
              </ul>
            </div>

            <p className="mb-6">
              Zeturf's holiday racing festival represents a landmark achievement in seasonal promotions, combining
              unprecedented financial commitments with technological innovation and international accessibility.
              This strategic initiative not only creates exceptional betting opportunities but also establishes
              new industry standards that will influence racing entertainment for years to come.
            </p>

            <p>
              For UK bettors, this festival represents access to continental racing excellence and life-changing
              betting opportunities that were previously unavailable in domestic markets. Zeturf's commitment to
              innovation and player-centric development ensures that this holiday spectacular delivers genuine
              value that transcends traditional seasonal promotions, creating lasting benefits for participants
              across the international betting community.
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ZeturfNews;
